// ============================================================================
// FNE-BRIDGE - Store Notifications (Zustand)
// ============================================================================

import { create } from 'zustand';
import type { Notification } from '../types/models';

// ============================================================================
// Types du state
// ============================================================================

interface NotificationState {
  // --- Liste ---
  notifications: Notification[];
  notificationsLoading: boolean;
  notificationsError: string | null;
  notificationsTotal: number;
  notificationsPage: number;
  notificationsLimit: number;

  // --- Compteur non lues ---
  unreadCount: number;

  // --- Actions ---
  setNotifications: (notifications: Notification[], total: number) => void;
  setNotificationsLoading: (loading: boolean) => void;
  setNotificationsError: (error: string | null) => void;
  setNotificationsPage: (page: number) => void;
  setUnreadCount: (count: number) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  addNotification: (notification: Notification) => void;
  removeNotification: (id: string) => void;
  reset: () => void;
}

// ============================================================================
// Store
// ============================================================================

export const useNotificationStore = create<NotificationState>()((set) => ({
  notifications: [],
  notificationsLoading: false,
  notificationsError: null,
  notificationsTotal: 0,
  notificationsPage: 1,
  notificationsLimit: 20,
  unreadCount: 0,

  setNotifications: (notifications, total) =>
    set({ notifications, notificationsTotal: total, notificationsLoading: false }),
  setNotificationsLoading: (notificationsLoading) => set({ notificationsLoading }),
  setNotificationsError: (notificationsError) =>
    set({ notificationsError, notificationsLoading: false }),
  setNotificationsPage: (notificationsPage) => set({ notificationsPage }),

  setUnreadCount: (unreadCount) => set({ unreadCount }),

  markAsRead: (id) =>
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, est_lue: true, lue_le: new Date().toISOString() } : n
      ),
      unreadCount: Math.max(0, state.unreadCount - 1),
    })),

  markAllAsRead: () =>
    set((state) => ({
      notifications: state.notifications.map((n) => ({
        ...n,
        est_lue: true,
        lue_le: n.lue_le || new Date().toISOString(),
      })),
      unreadCount: 0,
    })),

  addNotification: (notification) =>
    set((state) => ({
      notifications: [notification, ...state.notifications],
      notificationsTotal: state.notificationsTotal + 1,
      unreadCount: notification.est_lue ? state.unreadCount : state.unreadCount + 1,
    })),

  removeNotification: (id) =>
    set((state) => {
      const removed = state.notifications.find((n) => n.id === id);
      return {
        notifications: state.notifications.filter((n) => n.id !== id),
        notificationsTotal: state.notificationsTotal - 1,
        unreadCount: removed && !removed.est_lue
          ? Math.max(0, state.unreadCount - 1)
          : state.unreadCount,
      };
    }),

  reset: () =>
    set({
      notifications: [],
      notificationsLoading: false,
      notificationsError: null,
      notificationsTotal: 0,
      notificationsPage: 1,
      unreadCount: 0,
    }),
}));
